import { Injectable } from '@angular/core';
import { FinancialStatementService } from '../common/financial-statement.service';
import { InvestorCapitalAccountDTO } from '../../models/DTOs/capital-account/InvestorCapitalAccountDTO';
import { BehaviorSubject, firstValueFrom, lastValueFrom } from 'rxjs';
import { NOTIFICATIONS, NotificationsService } from '../../../../core/service/notifications.service';
import { InvestorCapitalAccount } from '../../models/capital-account/InvestorCapitalAccount';
import { ItemAllocationMethod } from '../../models/capital-account/ItemAllocationMethod';
import { ItemAllocationMethodDTO } from '../../models/DTOs/capital-account/ItemAllocationMethodDTO';
import { InvestorCapitalAccountItemValue } from '../../models/capital-account/InvestorCapitalAccountItemValue';
import { InvestorCapitalAccountItemValueDTO } from '../../models/DTOs/capital-account/InvestorCapitalAccountItemValueDTO';
import { CapitalAccountBackendService } from './capital-account-backend.service';
import { CustomAllocation } from '../../models/capital-account/CustomAllocation';
import { CustomAllocationDTO } from '../../models/DTOs/capital-account/CustomAllocationDTO';
import { CustomAllocationValueDTO } from '../../models/DTOs/capital-account/CustomAllocationValueDTO';
import { CustomAllocationValue } from '../../models/capital-account/CustomAllocationValue';
import { Investor } from '../../../investors-and-net-irr/dto/InvestorContribution';
import { InvestorService } from '../../../investors-and-net-irr/service/InvestorService';
import { ExcelService } from '../../../excel';
import { ExcelTemplateName } from '../../../excel/interfaces';
import { FinancialStatementPart } from '../../models/common/FinancialStatementPart';
import { cloneDeep } from 'lodash';
import { FinancialStatement } from '../../models/common/FinancialStatement';
import { CapitalAccountCalculationsService } from './capital-account-calculations/capital-account-calculations.service';
import { FinancialStatementItem } from '../../models/common/FinancialStatementItem';
import {
  AllocationMethod,
  CapitalAccountData, CarriedInterestAllocationMethod, CarriedInterestCA,
  CustomAllocationUpdate,
  InvestorDataCA,
  ItemAllocationUpdate,
} from '../../utils/capital-account/utils';
import {
  ItemManualValueData,
} from '../../../excel/templates/financial-statement/capital-account/item-manual-values-parser';
import { InvestorCapitalAccountCarriedInterest } from '../../models/capital-account/InvestorCapitalAccountCarriedInterest';
import { InvestorCapitalAccountCarriedInterestDTO } from '../../models/DTOs/capital-account/InvestorCapitalAccountCarriedInterestDTO';
import { CarriedInterestSelectorData } from '../../models/DTOs/capital-account/CarriedInterestSelectorData';
import { CarriedInterestSelectorDataDTO } from '../../models/DTOs/capital-account/CarriedInterestSelectorDataDTO';
import { accumulatedProfitAndLoss, baseFinancialStatementItems } from '../../utils/common/utilityClasses';
import { FsGettersSettersService } from '../common/fs-getters-setters.service';

@Injectable()

export class CapitalAccountService {
  private _fsUUID: BehaviorSubject<string> = new BehaviorSubject<string>(undefined);

  constructor(
    private financialStatementService: FinancialStatementService,
    private capitalAccountBackendService: CapitalAccountBackendService,
    private notificationsService: NotificationsService,
    private fundInvestorService: InvestorService,
    private excelService: ExcelService,
    private calculationsService: CapitalAccountCalculationsService,
    private fsGetterSetterService: FsGettersSettersService,
  ) {
    this.fsGetterSetterService.financialStatement.subscribe((fs) => {
      if (!fs) return;
      this._fsUUID.next(fs.uuid);
    });
  }

  async initCapitalAccountData(fs: FinancialStatement): Promise<CapitalAccountData> {
    if (!this._fsUUID.value || this._fsUUID.value !== fs.uuid) {
      this._fsUUID.next(fs.uuid);
    }
    const capitalAccountData = new CapitalAccountData();

    this.cloneAndFilterData(fs.balanceSheetParts[2], fs.profitLossParts, capitalAccountData);
    this.setTvpiIndexes(capitalAccountData);
    this.sortInvestorsByGpStatusAndLastDigits(fs.investors);
    this.calculationsService.calculateTotalCommitment(fs.investors, capitalAccountData.totals);
    await this.populateInvestorsData(capitalAccountData, fs);
    this.calculateTotals(capitalAccountData);
    return capitalAccountData;
  }

  async saveCapitalAccountInvestors(investors: InvestorCapitalAccountDTO[]): Promise<void> {
    if (this.financialStatementService.checkLockStatusOfFinancialStatement()) {
      return;
    }
    try {
      const result = await firstValueFrom(
        this.capitalAccountBackendService.saveCapitalAccountInvestors(investors, this._fsUUID.value),
      );
      this.notificationsService.success(NOTIFICATIONS.SAVE_SUCCESS);
      return result;
    } catch (error) {
      this.notificationsService.error(NOTIFICATIONS.INVESTORS_UPLOAD_ERROR);
    }
  }

  async deleteInvestors(investors: InvestorCapitalAccountDTO[]): Promise<void> {
    if (this.financialStatementService.checkLockStatusOfFinancialStatement()) {
      return;
    }
    try {
      return await firstValueFrom(this.capitalAccountBackendService.deleteCapitalAccountInvestors(investors));
    } catch (error) {
      this.notificationsService.error(NOTIFICATIONS.INVESTORS_DELETE_ERROR);
    }
  }

  async getCapitalAccountInvestors(): Promise<InvestorCapitalAccount[]> {
    try {
      return InvestorCapitalAccountDTO.arrayToInvestorCapitalAccountArray(
        await firstValueFrom(this.capitalAccountBackendService.getCapitalAccountInvestors(this._fsUUID.value)),
      );
    } catch (error) {
      this.notificationsService.error(NOTIFICATIONS.INVESTORS_LOAD_ERROR);
      throw error;
    }
  }

  async getAllocationMethodsForItems(): Promise<ItemAllocationMethod[]> {
    try {
      return ItemAllocationMethodDTO.toItemAllocationMethodList(
        await lastValueFrom(this.capitalAccountBackendService.getAllocationMethodsForItems(this._fsUUID.value)),
      );
    } catch (error) {
      this.notificationsService.error(NOTIFICATIONS.ITEMS_ALLOCATION_METHODS_LOAD_ERROR);
      return [];
    }
  }

  async setItemAllocationMethods(itemMethodDTOs: ItemAllocationMethodDTO[]): Promise<void> {
    try {
      await firstValueFrom(
        this.capitalAccountBackendService.setItemAllocationMethods(itemMethodDTOs, this._fsUUID.value),
      );
    } catch (error) {
      this.notificationsService.error(NOTIFICATIONS.ITEMS_ALLOCATION_METHOD_SET_ERROR);
    }
  }

  async getInvestorCapitalAccountItemValues(): Promise<InvestorCapitalAccountItemValue[]> {
    try {
      return InvestorCapitalAccountItemValueDTO.arrayToInvestorCapitalAccountItemValuesList(
        await firstValueFrom(this.capitalAccountBackendService.getInvestorCapitalAccountItemValues(this._fsUUID.value)),
      ) || [];
    } catch (error) {
      this.notificationsService.error(NOTIFICATIONS.ITEM_VALUES_LOAD_ERROR);
      throw error;
    }
  }

  async setInvestorCapitalAccountItemValues(investorCapitalAccountItemValues: InvestorCapitalAccountItemValue[],
  ): Promise<void> {
    try {
      const dtos = InvestorCapitalAccountItemValue
        .arrayToInvestorCapitalAccountItemMethodDTOList(investorCapitalAccountItemValues);
      await firstValueFrom(this.capitalAccountBackendService.setInvestorCapitalAccountItemValues(dtos));
    } catch (error) {
      this.notificationsService.error(NOTIFICATIONS.ITEM_VALUES_UPLOAD_ERROR);
    }
  }

  async getCustomAllocations(): Promise<CustomAllocation[]> {
    try {
      const customAllocations = CustomAllocationDTO.toEntityList(
        await firstValueFrom(this.capitalAccountBackendService.getCustomAllocations(this._fsUUID.value)),
      );
      return customAllocations.sort((a, b) => a.orderInTable - b.orderInTable);
    } catch (error) {
      this.notificationsService.error(NOTIFICATIONS.CUSTOM_ALLOCATIONS_LOAD_ERROR);
      return [];
    }
  }

  async setCustomAllocations(customAllocations: CustomAllocation[]): Promise<void> {
    try {
      await firstValueFrom(this.capitalAccountBackendService.setCustomAllocations(customAllocations, this._fsUUID.value));
      this.notificationsService.success(NOTIFICATIONS.SAVE_SUCCESS);
    } catch (error) {
      this.notificationsService.error(NOTIFICATIONS.CUSTOM_ALLOCATIONS_UPLOAD_ERROR);
    }
  }

  async deleteCustomAllocation(customAllocation: CustomAllocation): Promise<void> {
    try {
      await firstValueFrom(this.capitalAccountBackendService.deleteCustomAllocation(CustomAllocation.toDTO(customAllocation)));
      this.notificationsService.success(NOTIFICATIONS.SAVE_SUCCESS);
    } catch (error) {
      this.notificationsService.error(NOTIFICATIONS.CUSTOM_ALLOCATION_DELETE_ERROR);
    }
  }

  async getCustomAllocationValues(): Promise<CustomAllocationValue[]> {
    try {
      return CustomAllocationValueDTO.toEntityList(
        await firstValueFrom(this.capitalAccountBackendService.getCustomAllocationValues(this._fsUUID.value)),
      );
    } catch (error) {
      this.notificationsService.error(NOTIFICATIONS.CUSTOM_ALLOCATION_VALUES_LOAD_ERROR);
    }
  }

  async setCustomAllocationValues(customAllocationValues: CustomAllocationValue[]): Promise<void> {
    try {
      await firstValueFrom(
        this.capitalAccountBackendService.setCustomAllocationValues(CustomAllocationValue.toDTOList(customAllocationValues)),
      );
    } catch (error) {
      this.notificationsService.error(NOTIFICATIONS.CUSTOM_ALLOCATION_VALUES_UPLOAD_ERROR);
    }
  }

  sortInvestorsByGpStatusAndLastDigits(
    investors: InvestorCapitalAccountDTO[] | InvestorCapitalAccount[],
  ): InvestorCapitalAccountDTO[] | InvestorCapitalAccount[] {
    if (!investors.length) {
      return [];
    }
    return investors.sort((a, b) => {
      if (a.isGP !== b.isGP) {
        return a.isGP ? 1 : -1; // 'gp=true' after 'gp=false'
      }
      // get InvestorIDs (after the last dash) and convert them to numbers for comparison
      const lastNumberA = Number(a.code.split('-').pop());
      const lastNumberB = Number(b.code.split('-').pop());
      return lastNumberA - lastNumberB;
    });
  }

  getInvestors(): InvestorCapitalAccount[] {
    return this.financialStatementService.financialStatement.investors;
  }

  async pullInvestorsFromContribution(): Promise<InvestorCapitalAccountDTO[]> {
    const pulledInvestors: Investor[] = await firstValueFrom(
      this.fundInvestorService.getByFund(this.financialStatementService.financialStatement.ownershipUuid),
    );
    return InvestorCapitalAccountDTO.fromInvestorArray(pulledInvestors);
  }

  async exportExcel(
    capitalAccountData?: CapitalAccountData,
  ): Promise<void> {
    if (!capitalAccountData) {
      capitalAccountData = await this.initCapitalAccountData(this.financialStatementService.financialStatement);
    }
    const period = this.financialStatementService.financialStatement.period.toString();
    const fundName = this.financialStatementService.ownershipName;
    const fileName = `${period.toString()}/${fundName}/capital-account.xlsx`;
    this.excelService.exportByTemplate(capitalAccountData, fileName, ExcelTemplateName.capitalAccount);
  }

  async updateInvestor(investor: InvestorCapitalAccount): Promise<void> {
    try {
      await firstValueFrom(
        this.capitalAccountBackendService.updateInvestor(InvestorCapitalAccount.toDTO(investor)),
      );
    } catch (error) {
      this.notificationsService.error(NOTIFICATIONS.NETWORK_ERROR);
    }
  }

  separateItemsAndCustomAllocationsUpdates(capAccData: CapitalAccountData,
    itemManualValueDataArray: ItemManualValueData[]): [ItemAllocationUpdate[], CustomAllocationUpdate[]] {
    const itemAllocUpdates: ItemAllocationUpdate[] = [];
    const customAllocUpdates: CustomAllocationUpdate[] = [];

    for (const itemManualValueData of itemManualValueDataArray) {
      const itemAllocationMethod = this.findItemAllocMethodByName(capAccData, itemManualValueData.itemName);
      const customAllocation = this.findCustomAllocationByName(capAccData, itemManualValueData.itemName);

      if (itemAllocationMethod) {
        if (itemAllocationMethod.method !== AllocationMethod.MANUAL) {
          this.notificationsService.info(
            { title: itemManualValueData.itemName, message: 'Item allocation method is not manual. Please, set it to manual first.' },
          );
          continue;
        }
        itemAllocUpdates.push({ itemManualValueData, itemAllocationMethod });
      } else if (customAllocation) {
        customAllocUpdates.push({ itemManualValueData, customAllocation });
      } else {
        this.notificationsService.info({ title: itemManualValueData.itemName, message: 'Item not found.' });
      }
    }

    return [itemAllocUpdates, customAllocUpdates];
  }

  async setCarriedInterestValues(carriedInterests: InvestorCapitalAccountCarriedInterest[]): Promise<void> {
    try {
      await lastValueFrom(this.capitalAccountBackendService.setCarriedInterestValues(
        InvestorCapitalAccountCarriedInterest.arrayTODTOList(carriedInterests)),
      );
    } catch (error) {
      this.notificationsService.error(NOTIFICATIONS.NETWORK_ERROR);
    }
  }

  async setCarriedInterestSelectorData(selectorData: CarriedInterestSelectorData): Promise<void> {
    try {
      await firstValueFrom(this.capitalAccountBackendService.setCarriedInterestSelectorData(selectorData.toDTO(), this._fsUUID.value));
    } catch (error) {
      this.notificationsService.error(NOTIFICATIONS.NETWORK_ERROR);
    }
  }

  pushFsWithUpdatedInvestors(fs: FinancialStatement): void {
    this.fsGetterSetterService.setFinancialStatement(fs);
  }

  /** data is cloned to be filtered without modifying the original data from assembly */
  private cloneAndFilterData(
    partnersCapital: FinancialStatementPart, pl: FinancialStatementPart[], capitalAccountData: CapitalAccountData,
  ): void {
    const plCopy = cloneDeep(pl);
    plCopy.forEach((part) => {
      part.sums.balance *= -1; // multiplying the balance by -1
      part.financialStatementItems = part.financialStatementItems.filter((item) => {
        if (item.financialStatementAccounts.length > 0) {
          item.sums.balance *= -1; // multiplying the balance by -1
          return true;
        }
        return false;
      });
    });

    const partnersCapitalCopy = cloneDeep(partnersCapital);
    partnersCapitalCopy.sums.balance *= -1; // multiplying the balance by -1
    partnersCapitalCopy.financialStatementItems = partnersCapitalCopy.financialStatementItems.filter(
      (item) => {
        // "Due to capital contribution" and "Contribution received in advance" should be always present in table as per Sanya Khenkin
        if (item.name === baseFinancialStatementItems.partnersCapitalItems[1] ||
          item.name === baseFinancialStatementItems.partnersCapitalItems[2]) {
          item.sums.balance *= -1;
          return true;
        }
        // other items should be present only if they have accounts (accumulatedProfitAndLoss should be excluded)
        if (item.financialStatementAccounts.length > 0 && item.name !== accumulatedProfitAndLoss) {
          item.sums.balance *= -1; // multiplying the balance by -1
          return true;
        }
        return false;
      },
    );

    [capitalAccountData.partnersCapital, capitalAccountData.profits, capitalAccountData.losses] =
      [partnersCapitalCopy, plCopy[0], plCopy[1]];
  }

  private async fetchItemAllocationMethods(capitalAccountData: CapitalAccountData): Promise<void> {
    const newAllocationMethods: ItemAllocationMethod[] = [];
    let itemAllocationMethods: ItemAllocationMethod[] = await this.getAllocationMethodsForItems();
    const processMissingItem = (item: FinancialStatementItem): void => {
      if (!itemAllocationMethods.find((itemAllocationMethod) => itemAllocationMethod.item.id === item.id)) {
        const newItemMethod = new ItemAllocationMethod(null, item, AllocationMethod.HOLDING);
        itemAllocationMethods.push(newItemMethod);
        newAllocationMethods.push(newItemMethod);
      }
    };

    capitalAccountData.partnersCapital.financialStatementItems.forEach(processMissingItem);
    capitalAccountData.profits.financialStatementItems.forEach(processMissingItem);
    capitalAccountData.losses.financialStatementItems.forEach(processMissingItem);

    if (newAllocationMethods.length) {
      await this.setItemAllocationMethods(ItemAllocationMethod.toDTOList(newAllocationMethods));
      itemAllocationMethods = await this.getAllocationMethodsForItems();
    }
    capitalAccountData.itemAllocationMethods = itemAllocationMethods;
    capitalAccountData.itemAllocationMethods.forEach((allocMethod) => {
      capitalAccountData.itemToAllocationMethod[allocMethod.item.id] = allocMethod.method;
    });
  }

  /**
   * Main method that populates the investorsData array which is used to easily navigate and display data in the HTML.
   *
   * The investorsData array is built based on the financialStatement.investors array.
   * For each investor in the financialStatement, a corresponding InvestorData object is created.
   * This object is structured to provide all the necessary information for a single investor:
   * - Investor's data (name, ID, GP status, commitment, etc.)
   * - Calculated item values for the investor.
   * - Manual values entered for the investor.
   * - Holding and Holding without GP values.
   */
  private async populateInvestorsData(capitalAccountData: CapitalAccountData, fs: FinancialStatement): Promise<void> {
    await this.fetchItemAllocationMethods(capitalAccountData);
    capitalAccountData.manualValues = await this.getInvestorCapitalAccountItemValues();
    capitalAccountData.customAllocations = await this.getCustomAllocations();
    capitalAccountData.customAllocationValues = await this.getCustomAllocationValues();
    await this.setupCarriedInterest(capitalAccountData);

    capitalAccountData.investorsData = fs.investors.map((investor) => {
      const investorData = new InvestorDataCA(investor);
      this.processInvestorItems(investorData, capitalAccountData.partnersCapital.financialStatementItems, capitalAccountData);
      this.processInvestorItems(investorData, capitalAccountData.profits.financialStatementItems, capitalAccountData);
      this.processInvestorItems(investorData, capitalAccountData.losses.financialStatementItems, capitalAccountData);
      this.processCustomAllocations(investorData, capitalAccountData);
      this.processCarriedInterests(investorData, capitalAccountData.carriedInterest);

      return investorData;
    });

    this.setHoldings(capitalAccountData);
  }

  private async setupCarriedInterest(capitalAccountData: CapitalAccountData): Promise<void> {
    capitalAccountData.carriedInterest = new CarriedInterestCA();
    capitalAccountData.carriedInterest.dbInvestorsValues = await this.getCarriedInterestValues();
    capitalAccountData.carriedInterest.dbSelectorData = await this.getCarriedInterestSelectorData();
    capitalAccountData.carriedInterest.selector.selectedMethod = capitalAccountData.carriedInterest.dbSelectorData.selectedAllocationMethod;
    capitalAccountData.carriedInterest.manualPct = capitalAccountData.carriedInterest.dbSelectorData.manualPct;
  }

  private processInvestorItems(investorData: InvestorDataCA, items: FinancialStatementItem[], capAccData: CapitalAccountData): void {
    items.forEach((item) => {
      const itemValue = this.calculationsService.calculateItemValue(item, investorData.investor, capAccData);
      const manualValue = this.getManualValue(investorData.investor, item, capAccData);
      const allocationMethod = capAccData.itemAllocationMethods.find((method) => method.item.id === item.id)?.method;

      investorData.itemValue[item.id] = itemValue;
      investorData.manualValue[item.id] = manualValue;
      investorData.allocationMethod[item.id] = allocationMethod;
    });
  }

  private getManualValue(inv: InvestorCapitalAccount, item: FinancialStatementItem, capAccData: CapitalAccountData): number | undefined {
    const investorItemValue = capAccData.manualValues.find(
      (value) => value.investor.code === inv.code && value.itemAllocationMethod.item.id === item.id,
    );
    return investorItemValue?.manualValue;
  }

  private processCustomAllocations(investorData: InvestorDataCA, capitalAccountData: CapitalAccountData): void {
    capitalAccountData.customAllocations.forEach((customAllocation) => {
      // get custom allocation value for this investor
      const customAllocationValue = capitalAccountData.customAllocationValues?.find((value) =>
        value.customAllocation.uuid === customAllocation.uuid && value.investor.uuid === investorData.investor.uuid);
      // assign it to the investor data
      investorData.customAllocationValue[customAllocation.uuid] = customAllocationValue?.manualValue;
    });
  }

  private setHoldings(capitalAccountData: CapitalAccountData): void {
    capitalAccountData.totals.holding = { all: 0, gps: 0, lps: 0 };
    capitalAccountData.investorsData.forEach((investorData) => {
      investorData.holding = this.calculationsService.calculateHolding(investorData.investor, capitalAccountData.totals);
      investorData.holdingWithoutGP = this.calculationsService.calculateHolding(investorData.investor, capitalAccountData.totals, true);
      capitalAccountData.totals.holding.all += investorData.holding;
      capitalAccountData.totals.holding[investorData.investor.isGP ? 'gps' : 'lps'] += investorData.holding;
    });
  }

  private findItemAllocMethodByName(capAccData: CapitalAccountData, itemName: string): ItemAllocationMethod | undefined {
    return capAccData.itemAllocationMethods.find((method) => method.item.name.toLowerCase() === itemName.toLowerCase());
  }

  private findCustomAllocationByName(capAccData: CapitalAccountData, allocationName: string): CustomAllocation | undefined {
    return capAccData.customAllocations.find((allocation) => allocation.name.toLowerCase() === allocationName.toLowerCase());
  }

  private async getCarriedInterestValues(): Promise<InvestorCapitalAccountCarriedInterest[]> {
    try {
      return InvestorCapitalAccountCarriedInterestDTO.toEntityList(
        await firstValueFrom(this.capitalAccountBackendService.getCarriedInterestValues(this._fsUUID.value)),
      );
    } catch (error) {
      this.notificationsService.error(NOTIFICATIONS.NETWORK_ERROR);
    }
  }

  private processCarriedInterests(investorData: InvestorDataCA, carriedInterest: CarriedInterestCA): void {
    const investorCarriedInterest = carriedInterest.dbInvestorsValues.find((ci) => ci.investor.uuid === investorData.investor.uuid);
    if (!investorCarriedInterest) {
      return;
    }
    investorData.carriedInterest.manualValue = investorCarriedInterest.manualValue;
    investorData.carriedInterest.manualPct = investorCarriedInterest.manualPct;
  }

  private async getCarriedInterestSelectorData(): Promise<CarriedInterestSelectorData> {
    try {
      let selectorData: CarriedInterestSelectorDataDTO = await firstValueFrom(
        this.capitalAccountBackendService.getCarriedInterestSelectorData(this._fsUUID.value));
      if (!selectorData) {
        await this.setCarriedInterestSelectorData(new CarriedInterestSelectorData(null, CarriedInterestAllocationMethod.CALCULATED, null));
        selectorData = await firstValueFrom(this.capitalAccountBackendService.getCarriedInterestSelectorData(this._fsUUID.value));
      }
      return CarriedInterestSelectorDataDTO.toEntity(selectorData);
    } catch (error) {
      this.notificationsService.error(NOTIFICATIONS.NETWORK_ERROR);
    }
  }

  private setTvpiIndexes(capitalAccountData: CapitalAccountData): void {
    capitalAccountData.partnersCapital.financialStatementItems.forEach((item) => {
      switch (item.name) {
        case baseFinancialStatementItems.partnersCapitalItems[0]:
          capitalAccountData.tvpiItemsIds.contribution = item.id;
          break;
        case baseFinancialStatementItems.partnersCapitalItems[1]:
          capitalAccountData.tvpiItemsIds.dueToCapitalContribution = item.id;
          break;
        case baseFinancialStatementItems.partnersCapitalItems[2]:
          capitalAccountData.tvpiItemsIds.contributionReceivedInAdvance = item.id;
          break;
        case baseFinancialStatementItems.partnersCapitalItems[3]:
          capitalAccountData.tvpiItemsIds.distribution = item.id;
          break;
        default:
          break;
      }
    });
  }

  private calculateTotals(capitalAccountData: CapitalAccountData): void {
    this.calculationsService.calculateItemTotals(capitalAccountData);
    this.calculationsService.calculateCustomAllocationTotals(capitalAccountData);
    this.calculationsService.calculateCarriedInterest(capitalAccountData);
  }
}
